/**
 * File a dispute against a rental.
 *
 * Entry point: a completed rental where the host or guest wants to
 * report damage, excessive mileage, billing issue, etc. Captures up
 * to 10 evidence photos, a description, an optional amount, and the
 * category. Submits via disputeService.fileDispute which uploads
 * photos + inserts the rental_disputes row with a 72h response deadline.
 */
import { useCallback, useState } from 'react'
import { View, Text } from 'react-native'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { AlertTriangle, CheckCircle } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { CategoryPicker } from '@/components/disputes/CategoryPicker'
import { EvidencePicker, type EvidenceItem } from '@/components/disputes/EvidencePicker'
import { useFileDispute } from '@/lib/hooks/use-disputes'
import { useSemanticColors, SPACING, RADIUS, DURATION, FORM_FIELD_GAP } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { dollarsToCents } from '@/lib/utils/format'
import type { DisputeCategory } from '@/lib/types/dispute.types'

const MAX_PHOTOS = 10

export default function FileDisputeScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const params = useLocalSearchParams<{ bookingId?: string }>()

  const [category, setCategory] = useState<DisputeCategory | null>(null)
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [evidence, setEvidence] = useState<EvidenceItem[]>([])

  const { mutateAsync: fileDispute, isPending } = useFileDispute()

  const canSubmit =
    !!params.bookingId && !!category && description.trim().length >= 10 && evidence.length >= 1

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !params.bookingId || !category) return
    impact(ImpactStyle.Medium)
    try {
      await fileDispute({
        rentalId: params.bookingId,
        category,
        claimDescription: description.trim(),
        claimAmountCents: amount ? dollarsToCents(parseFloat(amount)) : 0,
        evidence,
      })
      void notification(NotificationType.Success)
      toast.success(t('disputes.filed_success'))
      router.back()
    } catch (err) {
      // Capture dispute filing failures instead of leaving only a dev console error and generic toast; no
      // Sentry signal on dispute filing failures (revenue-/trust-
      // critical flow). Surface real ServiceError reason when present
      // (RLS denial on rental_disputes, evidence upload to storage
      // failed, etc.) and capture to Sentry tagged with the rental.
      if (__DEV__) console.error('[File dispute]', err)
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('disputes.file_failed')))
      captureError(err, {
        screen: 'booking/dispute/file',
        action: 'file-dispute',
        rentalId: params.bookingId ?? '(missing)',
        category: category ?? '(missing)',
      })
    }
  }, [canSubmit, params.bookingId, category, description, amount, evidence, fileDispute, t, router])

  // One field-label style for the whole form. "Categoría" and "Evidencia
  // (fotos)" used to be 13pt bold near-black while the two TextInput labels
  // were 14pt regular — four peer labels drawn in two styles, so the form
  // read as two components stitched together. This matches TextInput's own
  // label exactly (FONTS.body / 14 / letterSpacing 0.2 / textPrimary).
  const fieldLabelStyle = {
    fontSize: 14,
    fontFamily: FONTS.body,
    letterSpacing: 0.2,
    color: colors.textPrimary,
  } as const

  // TextInput carries its own FORM_FIELD_GAP bottom margin so stacked forms
  // get the right rhythm without a wrapper. This ScrollView already spaces
  // its children by that same gap, and the two were stacking into a 56pt
  // hole below every input. Cancelling the intrinsic margin here leaves one
  // gap governing the whole form.
  const cancelIntrinsicFieldGap = { marginBottom: -FORM_FIELD_GAP } as const

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('disputes.file_title')} />

      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING.xl * 2,
          // One section gap for the whole form. It matches FORM_FIELD_GAP so
          // the inline groups and the TextInputs share a single rhythm.
          gap: FORM_FIELD_GAP,
        }}
        keyboardShouldPersistTaps="handled"
      >
        <Animated.View
          entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
          collapsable={false}
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: SPACING.md,
            backgroundColor: `${colors.warning}10`,
            padding: SPACING.md,
            borderRadius: RADIUS.lg,
          }}
        >
          {/* Icon tile — every other info card in the app leads with one. */}
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: RADIUS.md,
              backgroundColor: `${colors.warning}24`,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertTriangle size={20} color={colors.warning} strokeWidth={1.8} />
          </View>
          <Text
            style={{
              flex: 1,
              fontSize: 13,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              lineHeight: 18,
            }}
          >
            {t('disputes.file_intro')}
          </Text>
        </Animated.View>

        {/* ── Category ────────────────────────────────────────── */}
        <View style={{ gap: SPACING.md }}>
          <Text style={fieldLabelStyle}>{t('disputes.category_label')}</Text>
          <CategoryPicker value={category} onChange={setCategory} />
        </View>

        {/* ── Description ────────────────────────────────────── */}
        <View style={cancelIntrinsicFieldGap}>
          <TextInput
            label={t('disputes.description_label')}
            value={description}
            onChangeText={setDescription}
            placeholder={t('disputes.description_placeholder')}
            multiline
            numberOfLines={4}
            testID="disputes.description"
          />
        </View>

        {/* ── Amount ─────────────────────────────────────────── */}
        <View style={cancelIntrinsicFieldGap}>
          <TextInput
            label={t('disputes.amount_label')}
            value={amount}
            onChangeText={setAmount}
            placeholder={t('disputes.amount_placeholder')}
            keyboardType="decimal-pad"
            testID="disputes.amount"
          />
        </View>

        {/* ── Evidence photos ───────────────────────────────── */}
        <View style={{ gap: SPACING.md }}>
          <Text style={fieldLabelStyle}>{t('disputes.evidence_label')}</Text>
          <EvidencePicker
            items={evidence}
            onChange={setEvidence}
            max={MAX_PHOTOS}
            testID="disputes.evidence-picker"
          />
        </View>

        {/* ── Submit ────────────────────────────────────────── */}
        <Button
          title={t('disputes.submit')}
          onPress={handleSubmit}
          variant="primary"
          size="lg"
          disabled={!canSubmit || isPending}
          loading={isPending}
          testID="disputes.submit"
        />

        {canSubmit && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              padding: SPACING.sm,
              backgroundColor: `${colors.success}10`,
              borderRadius: RADIUS.md,
            }}
          >
            <CheckCircle size={16} color={colors.success} />
            <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
              {t('disputes.ready_to_submit')}
            </Text>
          </View>
        )}
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
